// Reporte de ventas de Farmacia Interna (Fase 3-4, 2026-09-11) — mismo motor
// que /admin/reportes del CRM (lib/reportes.ts:reporteVentas), acotado a
// origenModulo==='farmacia'. Cobrado y facturado se informan por separado —
// nunca se suman (uno es caja, el otro son documentos emitidos, incluida la
// parte a convenio que todavía no cobró la aseguradora).
import type { JSX } from 'react';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { reporteVentasFarmacia, type ReporteVentasFarmacia } from '@/lib/farmaciaInterna';

function formatoUsd(cents: number): string {
  return (cents / 100).toLocaleString('es-EC', { style: 'currency', currency: 'USD' });
}

function hoyLocal(): string {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

export function ReportesFarmacia(): JSX.Element {
  const { t } = useTranslation();
  const [desde, setDesde] = useState(hoyLocal());
  const [hasta, setHasta] = useState(hoyLocal());
  const [reporte, setReporte] = useState<ReporteVentasFarmacia | null>();

  async function buscar(): Promise<void> {
    setReporte(undefined);
    setReporte(await reporteVentasFarmacia(desde, hasta));
  }

  useEffect(() => {
    void buscar();
    // Solo al montar — "buscar" recarga explícitamente con los valores actuales de desde/hasta.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-end gap-2 rounded-lg border border-border p-4">
        <div>
          <Label className="mb-1 block text-xs">{t('farmaciaApp.reportes.desde')}</Label>
          <Input type="date" value={desde} onChange={(e) => setDesde(e.target.value)} />
        </div>
        <div>
          <Label className="mb-1 block text-xs">{t('farmaciaApp.reportes.hasta')}</Label>
          <Input type="date" value={hasta} onChange={(e) => setHasta(e.target.value)} />
        </div>
        <Button type="button" onClick={() => void buscar()}>
          {t('farmaciaApp.reportes.buscar')}
        </Button>
      </div>

      {reporte === undefined ? (
        <p className="text-sm text-hce-muted">{t('farmaciaApp.cargando')}</p>
      ) : reporte === null || !reporte.disponibleFacturacion ? (
        <p className="text-sm text-hce-muted">{t('farmaciaApp.noDisponible')}</p>
      ) : (
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
            <div className="rounded-lg border border-border p-4">
              <p className="text-xs text-hce-muted">{t('farmaciaApp.reportes.cobrado')}</p>
              <p className="text-xl font-semibold text-hce-ink">{formatoUsd(reporte.cobrado.totalCents)}</p>
              <p className="text-xs text-hce-muted">{t('farmaciaApp.reportes.cobros', { cantidad: reporte.cobrado.cantidad })}</p>
            </div>
            <div className="rounded-lg border border-border p-4">
              <p className="text-xs text-hce-muted">{t('farmaciaApp.reportes.facturado')}</p>
              <p className="text-xl font-semibold text-hce-ink">{formatoUsd(reporte.facturado.totalCents)}</p>
              <p className="text-xs text-hce-muted">{t('farmaciaApp.reportes.facturas', { cantidad: reporte.facturado.cantidad })}</p>
            </div>
            <div className="rounded-lg border border-border p-4">
              <p className="text-xs text-hce-muted">{t('farmaciaApp.reportes.alPaciente')}</p>
              <p className="text-xl font-semibold text-hce-ink">{formatoUsd(reporte.facturado.alPacienteCents)}</p>
            </div>
            <div className="rounded-lg border border-border p-4">
              <p className="text-xs text-hce-muted">{t('farmaciaApp.reportes.alConvenio')}</p>
              <p className="text-xl font-semibold text-hce-ink">{formatoUsd(reporte.facturado.alConvenioCents)}</p>
            </div>
          </div>

          {reporte.cobrado.porMetodo.length > 0 && (
            <div className="space-y-2">
              <p className="text-sm font-semibold text-hce-ink">{t('farmaciaApp.reportes.porMetodo')}</p>
              <div className="divide-y divide-border rounded-lg border border-border">
                {reporte.cobrado.porMetodo.map((m) => (
                  <div key={m.metodo} className="flex items-center justify-between gap-3 px-3 py-2 text-sm">
                    <span className="text-hce-ink">{m.label}</span>
                    <span className="text-hce-muted">
                      {formatoUsd(m.totalCents)} · {m.cantidad}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {reporte.iva.huecoIva && <p className="text-xs text-amber-700">{t('farmaciaApp.reportes.huecoIva')}</p>}
        </div>
      )}
    </div>
  );
}
